import * as THREE from 'three';
import type { Viewer } from '../../../../viewer/viewer';
import type { IPersona } from '../../../types';
import { DetectedFloorPlane } from '../../../../utils/floorDetection';
import { FloorplanPersona } from '../../../personas/implementations/FloorplanPersona';
import type { CommitContext } from './AnnotationKindAdapter';
import type { BasePlaneState, SelectionContextProvider } from './SelectionContextProvider';

/**
 * Floor-based selection context for the room and door/window LineTool
 * registrations. Thin reader: the host tool (a `FloorplanToolBase`) owns floor
 * isolation, so this context only translates the persona's selected floor into
 * the kind-neutral `BasePlaneState` the subtool draws on, and exposes the raw
 * floor/ceiling/group for the adapters' commit payloads.
 *
 * Symmetric with `SurfaceSelectionContext` (which reads the surface plane while
 * `SurfaceToolBase` owns surface isolation).
 *
 * The horizontal `BasePlaneState.normal` is world +Z and `origin` is the floor
 * center at floor height, so the modes' projection/zRange math is identical to
 * the pre-refactor behavior.
 */
/**
 * Extra z headroom added above and below the floor↔ceiling slab for the
 * position-map capture. Accurately fitted floor/ceiling planes sit ON their
 * point data, so an exact [floor, ceiling] range clips the floor and ceiling
 * points themselves out of the depth map — the margin keeps their full
 * scatter inside the encoding slab. Under true-ceiling semantics the ceiling
 * is the wall top, so [floor − margin, ceiling + margin] is exactly the wall
 * capture band; the inter-storey slab above needs no capture.
 */
const CAPTURE_Z_MARGIN_M = 0.3;

export class FloorSelectionContext implements SelectionContextProvider {
  private onChange: (() => void) | null = null;

  constructor(
    private readonly viewer: Viewer,
    private readonly persona: IPersona,
  ) {}

  private get floorplanPersona(): FloorplanPersona {
    return this.persona as FloorplanPersona;
  }

  attach(onChange: () => void): void {
    this.onChange = onChange;
    this.persona.addEventListener('floor_selection_changed', this.handleSelectionChanged);
    this.viewer.personaManager?.viewManager.addEventListener('view_changed', this.handleViewChanged);
  }

  detach(): void {
    this.persona.removeEventListener('floor_selection_changed', this.handleSelectionChanged);
    this.viewer.personaManager?.viewManager.removeEventListener('view_changed', this.handleViewChanged);
    this.onChange = null;
  }

  getBasePlaneState(): BasePlaneState | null {
    const floor = this.getFloor();
    if (!floor) return null;
    const floorHeight = floor.height;
    const ceilingHeight = this.getCeiling()?.height ?? null;
    return {
      origin: new THREE.Vector3(floor.center.x, floor.center.y, floorHeight),
      normal: new THREE.Vector3(0, 0, 1),
      captureZRange: ceilingHeight !== null ? [floorHeight - CAPTURE_Z_MARGIN_M, ceilingHeight + CAPTURE_Z_MARGIN_M] : null,
      contextKey: this.getGroupIdentifier(),
    };
  }

  getCommitContext(): CommitContext | null {
    const basePlane = this.getBasePlaneState();
    if (!basePlane) return null;
    return { basePlane };
  }

  /** Active floor for adapter calls that read the raw `DetectedFloorPlane`. */
  getFloor(): DetectedFloorPlane | null {
    return this.floorplanPersona.getSelectedFloorData()?.floor ?? null;
  }

  getCeiling(): DetectedFloorPlane | null {
    return this.floorplanPersona.getSelectedFloorData()?.ceiling ?? null;
  }

  getGroupIdentifier(): string | null {
    return this.floorplanPersona.getSelectedFloorGroupIdentifier();
  }

  private handleSelectionChanged = (): void => {
    this.onChange?.();
  };

  private handleViewChanged = (): void => {
    this.onChange?.();
  };
}
